<script setup>
import { INSTRUCTION_TEXTS } from "@/utils/constant";
import { onIdTokenChanged } from "firebase/auth";
import { createFirebaseSessionSync } from "@/lib/auth/firebaseSessionSync";
import { firebaseAuth } from "@/plugins/firebase";
// import useCustomCursor, { useCursorSmoke } from "./composables/useCustomCursor";
import { useDisplay } from "vuetify";
import { useHead } from "@vueuse/head";
import { fetchRiftTradeCurrency } from "@/api/riftTrade";
import {
  SITE_NAME,
  SITE_DESCRIPTION,
  SITE_URL,
  OG_IMAGE,
} from "@/utils/constant";
import { useGameStore } from "@/plugins/2.pinia/modules/game";

const preLoading = ref(false);

const route = useRoute();

const appStore = useAppStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const onboarding = useOnboardingStore();
const gameStore = useGameStore();
onboarding.load();
const { mobile } = useDisplay();

let stopFirebaseSync;
const firebaseSessionSync = createFirebaseSessionSync({
  apply(token, expiresAt) {
    authStore.accessToken = token;
    authStore.tokenExpiresAt = expiresAt;
  },
  isCurrentUser: user => firebaseAuth.currentUser === user,
  onError: () => console.error("Failed to sync Firebase auth state"),
});

onMounted(async () => {
  console.log(`Mode: ${import.meta.env.MODE}, Version: ${__APP_VERSION__}`);
  appStore.startLoadingScreen("preLoading", { blocking: true });
});

onUnmounted(() => {
  firebaseSessionSync.dispose();
  if (typeof stopFirebaseSync === "function") stopFirebaseSync();
});

watch(
  [
    () => appStore.isAppReady,
    () => authStore.isConnectedPN,
    () => userStore.particleUserInfo?.uuid,
    () => userStore.particleUserInfo?.token,
  ],
  async ([isAppReady]) => {
    console.log("App is ready:", isAppReady);
    if (typeof stopFirebaseSync === "function") stopFirebaseSync();
    stopFirebaseSync = undefined;
    firebaseSessionSync.invalidate();
    if (isAppReady) {
      console.log("Wallet authentication is ready");
      stopFirebaseSync = onIdTokenChanged(firebaseAuth, firebaseSessionSync.sync);

      await gameStore.ensureGamesLoaded();
      appStore.endLoadingScreen("preLoading");
      if (authStore.isConnectedPN && userStore.particleUserInfo?.uuid && userStore.particleUserInfo?.token) {
        try {
          await authStore.authenticateAndInitialize();
        } catch {
          // The auth store exposes a safe, visible error beside the login button.
          return;
        }
        if (!authStore.isLoggedIn) return;

        await fetchRiftTradeCurrency()
          .then((currency) => {
            if (currency && typeof currency === "object") {
              if (typeof currency.rockets !== "undefined") {
                userStore.seftRockets = Number(currency.rockets);
              }
              if (typeof currency.zaps !== "undefined") {
                userStore.seftZaps = Number(currency.zaps);
              }
            }
          })
          .catch(() => {});

        if (userStore.userInfo?.firstName && !onboarding.shown.home) {
          onboarding.showInstruction({
            text: INSTRUCTION_TEXTS.home,
            onClose: () => onboarding.markShown("home"),
          });
        }
      }
    }
  },
  // { immediate: true }
);

const canonicalUrl = computed(() =>
  new URL(route.fullPath, SITE_URL).toString(),
);
const structuredData = computed(() => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
}));

useHead(() => {
  const pageTitle = route.meta?.title || SITE_NAME;
  const pageDescription = route.meta?.description || SITE_DESCRIPTION;
  const titleTemplate = (title) =>
    title && title !== SITE_NAME ? `${title} | NEXUS` : SITE_NAME;

  return {
    title: pageTitle,
    titleTemplate,
    link: [
      {
        rel: "canonical",
        href: canonicalUrl.value,
        key: "canonical",
      },
    ],
    meta: [
      { name: "description", content: pageDescription, key: "description" },
      { property: "og:title", content: pageTitle, key: "og:title" },
      {
        property: "og:description",
        content: pageDescription,
        key: "og:description",
      },
      { property: "og:image", content: OG_IMAGE, key: "og:image" },
      { property: "og:url", content: canonicalUrl.value, key: "og:url" },
      { property: "og:type", content: "website", key: "og:type" },
      {
        name: "twitter:card",
        content: "summary_large_image",
        key: "twitter:card",
      },
      { name: "twitter:title", content: pageTitle, key: "twitter:title" },
      {
        name: "twitter:description",
        content: pageDescription,
        key: "twitter:description",
      },
      { name: "twitter:image", content: OG_IMAGE, key: "twitter:image" },
    ],
    script: [
      {
        type: "application/ld+json",
        children: JSON.stringify(structuredData.value),
        key: "ld:website",
      },
    ],
  };
});
</script>

<template>
  <VApp>
    <!-- <template v-if="preLoading">
      <v-skeleton-loader />
    </template> -->
    <div class="layout-wrapper">
      <RouterView />
      <AlertMessage />
      <SnackbarMessage />
      <LoadingOverlay />
      <InstructionDialog />
      <UserProfileDialog />
      <RewardsDialog />
    </div>

    <!-- <template v-if="!mobile">
      <div class="mouseCursor cursor-outer"></div>
      <div class="mouseCursor cursor-inner"></div>
    </template> -->
  </VApp>
</template>

<style>
.mouseCursor {
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 9999;
  transition:
    transform 0.12s ease-out,
    opacity 0.2s,
    box-shadow 0.2s;
  will-change: transform, opacity, box-shadow;
}

.cursor-outer {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 2px solid rgba(241, 59, 34, 0.9);
  box-shadow: 0 0 18px 6px rgba(243, 103, 32, 0.35);
  transform: translate(-50%, -50%) scale(1);
  opacity: 0.6;
  mix-blend-mode: screen;
}

.cursor-inner {
  width: 8px;
  height: 8px;
  background: #f13b22;
  border-radius: 50%;
  transform: translate(-50%, -50%) scale(1);
}

.cursor-hover.cursor-outer {
  /* transform: translate(-50%, -50%) scale(1.45); */
  opacity: 0.35;
  box-shadow: 0 0 22px 10px rgba(243, 103, 32, 0.5);
}
.cursor-hover.cursor-inner {
  transform: translate(-50%, -50%) scale(0.8);
}

.smoke {
  position: fixed;
  width: 40px;
  height: 40px;
  background: linear-gradient(180deg, rgba(203, 254, 28, 0) 40%, #f13b22 100%);
  pointer-events: none;
  border-radius: 50%;
  filter: blur(1px);
  opacity: 0.8;
  z-index: 9998;
  animation: fadeOutSmoke 1s ease-out forwards;
  will-change: transform, opacity;
}

@keyframes fadeOutSmoke {
  0% {
    transform: scale(1) translateY(0);
    opacity: 0.7;
  }
  100% {
    transform: scale(2) translateY(-20px);
    opacity: 0;
  }
}
</style>
